import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, notification } from 'antd'

import { useAuth } from '@/middlewares/auth/auth-context'
import { getCurrentUser, userUpdate } from '@/middlewares/services/user.services'
import Stripe from '@/components/stripe/Stripe'
import LoadingScreen from '@/components/common/LoadingScreen'
import CurrencyInput from '@/components/common/CurrencyInput'
import PhoneNumberInput from '@/components/common/PhoneNumberInput'
import OrganizerStatusTag from '@/components/Organizer/shared/OrganizerStatusTag'
import TranslationNS from '@/utils/Constants/TranslationNS'
import AccountPageHeader from '../AccountPageHeader'
import AccountField from '../AccountField'
import { parseInvoiceAddress } from '@/utils/invoiceAddress'

import '../myAccount.css'
import '../AccountDetails/accountDetails.css'
import './organisationDetails.css'

// antd tag colours the auth context still uses for the Stripe account status → the charter's state dots
const STATUS_DOT = { green: '--status-open', success: '--status-open', orange: '--status-closed', warning: '--status-closed', red: '--status-soldout', error: '--status-soldout' }

const stripCommas = (value) => (typeof value === 'string' ? value.replace(',', '') : value)

const toFormValues = (user) => {
    const settings = user?.organisation_settings || {}
    // Modif/621 object + legacy "street, city, country" string
    const address = parseInvoiceAddress(settings.invoice_address)
    return {
        organisation: user?.organisation || '',
        phoneNumber: settings.phone_number,
        street: address.street_number,
        city: address.city,
        country: address.country,
        zip_code: address.zip_code,
        currency: settings.currency,
        VAT_number: settings.VAT_number || '',
        taxe_rate: settings.taxe_rate ?? '',
    }
}

const normalizeForCompare = (values = {}) => ({
    organisation: values.organisation || '',
    phoneNumber: {
        code: values.phoneNumber?.code ?? '',
        phone: values.phoneNumber?.phone ?? '',
        short: values.phoneNumber?.short ?? '',
    },
    street: values.street || '',
    city: values.city || '',
    country: values.country || '',
    zip_code: values.zip_code || '',
    currency: values.currency || '',
    VAT_number: values.VAT_number || '',
    taxe_rate: String(values.taxe_rate ?? ''),
})

/**
 * Organizer details (My account › Organizer details, and the "complete your account" gate of
 * Create an event when `isModal`): organisation name, phone, invoice address (three fields stored
 * as one string), Stripe connection + currency, VAT number and tax rate. Same validations, same
 * `userUpdate` payload and the same modal callbacks as the legacy screen; the form follows the
 * Account details pattern (`.ac-form`, errors on the label row, Save / Cancel in the header).
 */
export default function OrganisationDetails({
    isModal = false,
    setCompleteAccount = () => {},
    setCreateCompetition = () => {},
}) {
    const [form] = Form.useForm()
    const { t } = useTranslation(TranslationNS.MY_ACCOUNT)

    const authContext = useAuth()

    const [user, setUser] = useState()
    const [savedSnapshot, setSavedSnapshot] = useState()
    const [isDirty, setIsDirty] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    // programmatic changes before the user touches the form (the phone input re-emits a normalised
    // number on mount, legacy behaviour kept by the save) are the baseline, not an edit
    const touched = useRef(false)

    useEffect(() => {
        const getUser = async () => {
            const response = await getCurrentUser()
            const userFound = response.data.data

            if (userFound) {
                setUser(userFound)
                const values = toFormValues(userFound)
                form.setFieldsValue(values)
                setSavedSnapshot(normalizeForCompare(values))
                setIsDirty(false)
                touched.current = false

                authContext.refreshStripeAccountStatus()
            } else {
                notification.error({
                    message: t('Oops, an error occurred !'),
                    duration: 0,
                })
            }
        }
        if (authContext.user.id) {
            getUser()
        }
    }, [authContext?.user])

    const syncDirty = (values) => {
        if (!savedSnapshot) return
        const next = normalizeForCompare({ ...form.getFieldsValue(true), ...values })
        if (!touched.current) {
            setSavedSnapshot(next)
            setIsDirty(false)
            return
        }
        setIsDirty(JSON.stringify(next) !== JSON.stringify(savedSnapshot))
    }

    const handleCancel = () => {
        if (!user) return
        form.setFieldsValue(toFormValues(user))
        form.setFields(Object.keys(toFormValues(user)).map((name) => ({ name, errors: [] })))
        setIsDirty(false)
    }

    /** The legacy notifications (one at a time, first failing rule). */
    const handleFinishFailed = ({ errorFields }) => {
        const message = errorFields?.[0]?.errors?.[0]
        if (message) notification.error({ message, duration: 0 })
    }

    const saveOrganisationProfile = async (values) => {
        let newUser = { ...user, organisation: values.organisation }

        if (!newUser.organisation_settings) newUser.organisation_settings = {}
        newUser.organisation_settings = {
            ...newUser.organisation_settings,
            invoice_address: {
                street_number: stripCommas(values.street),
                city: stripCommas(values.city),
                country: stripCommas(values.country),
                zip_code: stripCommas(values.zip_code),
            },
            phone_number: values.phoneNumber,
            VAT_number: values.VAT_number,
            taxe_rate: values.taxe_rate,
        }
        if (newUser.organisation_settings.stripe?.account_id && values.currency) {
            newUser.organisation_settings.currency = values.currency
        }

        setIsLoading(true)
        try {
            await userUpdate(authContext.user.id, {
                organisation: {
                    name: newUser.organisation,
                    settings: newUser.organisation_settings,
                },
            })
            authContext.updateUser(newUser)
            setUser(newUser)
            setSavedSnapshot(normalizeForCompare(toFormValues(newUser)))
            setIsDirty(false)
            notification.success({ message: t('Informations updated !') })
        } catch (error) {
            console.error('/saveOrganisationProfile - error', error)
            notification.error({
                message: error?.response?.data?.message || t('Oops, an error occurred !'),
                duration: 0,
            })
        }
        // legacy flow: the gate hands over to the create-event modal once Next is pressed
        if (isModal) {
            setCreateCompetition(true)
            setCompleteAccount(false)
        }
        setIsLoading(false)
    }

    if (!user) {
        return <LoadingScreen />
    }

    const stripeAccountId = user.organisation_settings?.stripe?.account_id
    const stripeStatus = authContext.stripeAccountStatus
    const formId = 'organisation-details-form'

    const requiredText = (message) => ({
        required: true,
        validator: (_, value) => (value?.trim?.() ? Promise.resolve() : Promise.reject(new Error(message))),
    })

    const formElement = (
        <Form
            id={formId}
            form={form}
            layout="vertical"
            requiredMark={false}
            disabled={isLoading}
            onValuesChange={(_, allValues) => syncDirty(allValues)}
            onPointerDownCapture={() => (touched.current = true)}
            onKeyDownCapture={() => (touched.current = true)}
            onFinish={saveOrganisationProfile}
            onFinishFailed={handleFinishFailed}
            className={`ac-form ${isModal ? 'ac-form--modal' : ''}`}
        >
            <AccountField label={t('Organisation name')} name="organisation" rules={[requiredText(t('Please enter your organisation name'))]}>
                <Input placeholder={t('Organisation')} />
            </AccountField>
            <AccountField
                label={t('Phone number')}
                name="phoneNumber"
                rules={[
                    {
                        required: true,
                        validator: (_, value) =>
                            value?.phone ? Promise.resolve() : Promise.reject(new Error(t('Please enter your phone number'))),
                    },
                ]}
            >
                <PhoneNumberInput />
            </AccountField>

            <h2 className="ac-form-h">{t('Address')}</h2>
            <AccountField label={t('Street and number')} name="street" normalize={stripCommas} rules={[requiredText(t('Please enter your full invoice address'))]}>
                <Input placeholder={t('Street')} />
            </AccountField>
            <AccountField label={t('City')} name="city" normalize={stripCommas} rules={[requiredText(t('Please enter your full invoice address'))]}>
                <Input placeholder={t('City')} />
            </AccountField>
            <AccountField label={t('Country')} name="country" normalize={stripCommas} rules={[requiredText(t('Please enter your full invoice address'))]}>
                <Input placeholder={t('Country')} />
            </AccountField>
            <AccountField label={t('Zip code')} name="zip_code" normalize={stripCommas} rules={[requiredText(t('Please enter your full invoice address'))]}>
                <Input placeholder={t('Zip code')} />
            </AccountField>

            <h2 className="ac-form-h">
                <img src="https://b.stripecdn.com/manage-statics-srv/assets/public/favicon.ico" alt="" className="ac-form-h-ico" />
                {t('Stripe')}
                {stripeAccountId ? <OrganizerStatusTag solid>{t('connected')}</OrganizerStatusTag> : null}
            </h2>
            <div className="ac-form-span ac-stripe">
                {stripeStatus ? (
                    <div className="ac-stripe-status">
                        <span className="od-st">
                            <span className="dot" style={{ background: `var(${STATUS_DOT[stripeStatus.color] || '--t-strong'}, #272727)` }} />
                            {t('status')} : {stripeStatus.label}
                        </span>
                        {stripeStatus.reason ? <p>{stripeStatus.reason}</p> : null}
                    </div>
                ) : null}
                {!user.organisation_settings?.stripe && (
                    <p className="ac-stripe-intro">
                        {t('Stripe is our payment partner. You can connect or create an account now to accept payments. You also can do it later.')}
                    </p>
                )}
                <Stripe user={user} />
            </div>
            {stripeAccountId ? (
                <AccountField label={t('Currency')} name="currency" valuePropName="currency" rules={[{ required: true }]}>
                    <CurrencyInput stripe_account_id={stripeAccountId} />
                </AccountField>
            ) : null}

            <h2 className="ac-form-h">{t('Billing information')}</h2>
            <AccountField label={t('VAT number (not required)')} name="VAT_number">
                <Input placeholder={t('VAT number')} />
            </AccountField>
            <AccountField
                label={t('Tax rate in your country % (not required)')}
                name="taxe_rate"
                normalize={(value) => (typeof value === 'string' ? value.replace(',', '.') : value)}
            >
                <Input suffix="%" type="number" placeholder={t('Tax rate')} />
            </AccountField>
        </Form>
    )

    if (isModal) {
        return (
            <section className="account-organisation account-organisation--modal">
                {isLoading ? <LoadingScreen /> : null}
                {formElement}
                <div className="ac-form-acts">
                    <button type="button" className="ac-btn ac-btn-ghost s-focus-ring" onClick={() => setCompleteAccount(false)}>
                        {t('Cancel')}
                    </button>
                    <button type="submit" form={formId} className="ac-btn ac-btn-primary s-focus-ring">
                        {t('Next')}
                    </button>
                </div>
            </section>
        )
    }

    return (
        <section className="account-organisation">
            {isLoading ? <LoadingScreen /> : null}
            <AccountPageHeader title={t('Organizer details')} subtitle={t('Your organisation, billing address and payments')}>
                {isDirty ? (
                    <>
                        <button type="button" onClick={handleCancel} className="ac-btn ac-btn-ghost ac-btn-sm s-focus-ring">
                            {t('Cancel')}
                        </button>
                        <button type="submit" form={formId} className="ac-btn ac-btn-primary ac-btn-sm s-focus-ring">
                            {t('Save')}
                        </button>
                    </>
                ) : (
                    <span className="ac-saved">{t('All Saved')}</span>
                )}
            </AccountPageHeader>
            {formElement}
        </section>
    )
}
